import { useCallback, useEffect, useState } from "react";

import ConfirmModal from "@/components/ConfirmModal";
import ErrorNotice from "@/components/ErrorNotice";
import { GithubMark } from "@/components/GithubBits";
import UseFromAgents from "@/components/UseFromAgents";
import {
  disconnectGithub,
  getDefaultApiKey,
  getGithubStatus,
  regenerateDefaultKey,
  setStoredApiKey,
  startGithubOAuth,
} from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { describeGithubError, type DescribedError } from "@/lib/github-errors";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import type { GithubStatus } from "@/lib/types";

function EyeIcon({ off }: { off?: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}
function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export default function SettingsPage() {
  useDocumentTitle("SkillPatch");
  const { user, accessToken } = useAuth();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const load = useCallback(async () => {
    if (!accessToken) return;
    const k = await getDefaultApiKey(accessToken).catch(() => null);
    if (k) {
      setApiKey(k.key);
      setStoredApiKey(k.key);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Don't decide logged-in vs logged-out until the persisted session has been
  // restored — otherwise a hard refresh flashes the "Log in" prompt for a few
  // ms before the token arrives and the real page renders.
  // Gating is handled by the <RequireAuth> route guard; kept as a defensive /
  // type-narrowing check (the page never mounts for a logged-out user).
  if (!user || !accessToken) return null;

  async function onCopy() {
    if (!apiKey) return;
    await navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function onRegenerate() {
    if (!accessToken) return;
    setConfirmRegen(false);
    setBusy(true);
    try {
      const k = await regenerateDefaultKey(accessToken);
      setApiKey(k.key);
      setStoredApiKey(k.key);
      setShow(false);
    } finally {
      setBusy(false);
    }
  }

  const masked = apiKey ? `${apiKey.slice(0, 8)}  ${"•".repeat(18)}  ${apiKey.slice(-8)}` : "";

  const btn = "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition";

  const initials = (user.display_name || user.email)
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const _toUtc = (s: string) => new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + "Z");
  const memberSince = _toUtc(user.created_at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <main className="mx-auto max-w-[1300px] px-10 py-10">
      <h1 className="font-display text-2xl font-bold">Settings</h1>

      <section className="mt-6">
        <h2 className="font-display text-lg font-semibold">Account Settings</h2>
        <p className="text-sm text-text/50">Manage your account settings and preferences</p>

        <div className="mt-4 divide-y divide-[var(--border)] rounded-2xl border border-[var(--border)] bg-[var(--glass)]">
          <div className="flex items-center gap-4 p-6">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-lg font-semibold text-white">
              {initials}
            </span>
            <div>
              <div className="text-lg font-semibold">{user.display_name}</div>
              <div className="text-sm text-text/50">Member since {memberSince}</div>
            </div>
          </div>

          <div className="flex items-center justify-between px-6 py-4">
            <span className="font-medium">Username</span>
            <span className="text-text/50">{user.display_name}</span>
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <span className="font-medium">Email address</span>
            <span className="text-text/50">{user.email}</span>
          </div>
        </div>
      </section>

      {/* Use SkillPatch from Agents card (shared with the Home feed) */}
      <UseFromAgents className="mt-8" />

      {/* API Management card */}
      <section className="mt-8 rounded-2xl border border-[var(--border)] bg-[var(--glass)] p-6">
        <h2 className="font-display text-lg font-semibold">API Management</h2>

        <div className="mt-5 flex flex-col gap-3 rounded-xl bg-white/[0.04] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <code className="font-mono text-sm tracking-wide text-text/90">
            {apiKey ? (show ? apiKey : masked) : "Loading…"}
          </code>

          <div className="flex shrink-0 items-center gap-2">
            <button onClick={() => setShow((s) => !s)} className={`${btn} bg-white/5 text-text/80 hover:bg-white/10`}>
              <EyeIcon off={show} />
              {show ? "Hide" : "Show"}
            </button>
            <button onClick={onCopy} className={`${btn} bg-primary text-white hover:opacity-90`}>
              <CopyIcon />
              {copied ? "Copied" : "Copy"}
            </button>
            <button onClick={() => setConfirmRegen(true)} disabled={busy} className={`${btn} bg-white/5 text-text/80 hover:bg-white/10 disabled:opacity-50`}>
              <RefreshIcon />
              {busy ? "…" : "Regenerate"}
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-text/40">
          Agents send this key as <code>X-API-Key</code>. Regenerating invalidates the old key immediately.
        </p>
      </section>

      {/* Connected accounts (GitHub) card */}
      <GithubConnectionCard accessToken={accessToken} />

      {confirmRegen && (
        <ConfirmModal
          title="Regenerate API key?"
          message="Your current API key stops working immediately. Any agent or tool using it will need the new key."
          confirmLabel="Regenerate"
          destructive
          busy={busy}
          onConfirm={onRegenerate}
          onCancel={() => setConfirmRegen(false)}
        />
      )}
    </main>
  );
}

const _toUtcDate = (s: string) => new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + "Z");

// Maps the ?github=error&reason=… flag GitHub's callback bounces back with onto a readable notice.
function oauthReasonNotice(reason: string): DescribedError {
  const detail =
    reason === "access_denied"
      ? "You cancelled the GitHub authorization."
      : reason === "invalid_state"
        ? "The connect request expired. Please try again."
        : reason === "exchange_failed"
          ? "GitHub declined the connection. Please try again."
          : reason === "server_error"
            ? "Something went wrong while completing the connection."
            : "GitHub couldn’t complete the connection.";
  return { tone: "error", title: "Couldn’t connect your GitHub account", detail, hint: "Please try connecting again." };
}

function GithubConnectionCard({ accessToken }: { accessToken: string }) {
  const [status, setStatus] = useState<GithubStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DescribedError | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const load = useCallback(async () => {
    const s = await getGithubStatus(accessToken).catch(() => null);
    if (s) setStatus(s);
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle the return leg of the OAuth redirect (…/settings?github=connected|error). On success
  // just reload the live status; on error surface why. Strip the query so a refresh won't repeat it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("github");
    if (!result) return;
    if (result === "error") setError(oauthReasonNotice(params.get("reason") || ""));
    else if (result === "connected") load();
    window.history.replaceState({}, "", window.location.pathname);
  }, [load]);

  async function onConnect() {
    setBusy(true);
    setError(null);
    try {
      const { authorize_url } = await startGithubOAuth(accessToken);
      window.location.href = authorize_url; // leave the app; GitHub redirects back to /settings
    } catch (e) {
      setError(describeGithubError(e, "connect"));
      setBusy(false); // only reached if we never navigated away
    }
  }

  async function onDisconnect() {
    setConfirmDisconnect(false);
    setBusy(true);
    setError(null);
    try {
      await disconnectGithub(accessToken);
      await load();
    } catch (e) {
      setError(describeGithubError(e, "disconnect"));
    } finally {
      setBusy(false);
    }
  }

  const accessSummary = status?.scopes?.includes("repo")
    ? "Read & write access to your repositories"
    : status?.scopes?.length
      ? `Access granted: ${status.scopes.join(", ")}`
      : "Repository access granted";

  return (
    <section className="mt-8 rounded-2xl border border-[var(--border)] bg-[var(--glass)] p-6">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
        <GithubMark size={18} /> GitHub
      </h2>
      <p className="mt-1 text-sm text-text/50">
        Connect your GitHub account to import skills straight from a repository and keep them in
        two-way sync.
      </p>

      {status?.connected && status.token_valid !== false ? (
        <div className="mt-5 flex flex-col gap-4 rounded-xl bg-white/[0.04] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            {status.avatar_url ? (
              <img src={status.avatar_url} alt="" className="h-10 w-10 shrink-0 rounded-full" />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10">
                <GithubMark size={18} />
              </span>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">@{status.login}</span>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Connected
                </span>
              </div>
              <div className="mt-0.5 text-xs text-text/50">
                {status.connected_at && `Connected ${_toUtcDate(status.connected_at).toLocaleDateString()}`}
                {` · ${status.linked_count} skill${status.linked_count === 1 ? "" : "s"} linked`}
              </div>
              <div className="mt-1 text-xs text-text/40">{accessSummary}</div>
              <a
                href="https://github.com/settings/applications"
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary transition hover:underline"
              >
                Review access on GitHub ↗
              </a>
            </div>
          </div>
          <button
            onClick={() => setConfirmDisconnect(true)}
            disabled={busy}
            className="shrink-0 rounded-lg border border-white/15 px-3 py-2 text-sm font-medium text-text/80 transition hover:bg-white/10 disabled:opacity-50"
          >
            {busy ? "…" : "Disconnect"}
          </button>
        </div>
      ) : (
        <div className="mt-5">
          {status?.connected && status.token_valid === false && (
            <div className="mb-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              Your GitHub connection for <b>@{status.login}</b> has expired or been revoked.
              Reconnect below — your linked skills stay linked.
            </div>
          )}
          {status && status.oauth_enabled === false ? (
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
              GitHub connections aren’t available right now. Please try again later.
            </p>
          ) : (
            <>
              <button
                onClick={onConnect}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
              >
                <GithubMark size={16} />
                {busy ? "Redirecting…" : status?.connected ? "Reconnect GitHub" : "Connect with GitHub"}
              </button>
              <p className="mt-3 text-xs leading-relaxed text-text/40">
                Authorize on GitHub in one click — nothing to paste.
              </p>
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-text/40">
                <LockIcon />
                Encrypted, never shared, revocable anytime.
              </p>
            </>
          )}
        </div>
      )}

      <ErrorNotice error={error} className="mt-3" />

      {confirmDisconnect && (
        <ConfirmModal
          title="Disconnect GitHub?"
          message={
            status?.linked_count
              ? `Your GitHub connection will be removed and ${status.linked_count} linked skill${
                  status.linked_count === 1 ? "" : "s"
                } will be unlinked (their sync webhooks are removed). Your skills and their versions stay on the platform.`
              : "Your GitHub connection will be removed. Your skills and their versions stay on the platform."
          }
          confirmLabel="Disconnect"
          destructive
          busy={busy}
          onConfirm={onDisconnect}
          onCancel={() => setConfirmDisconnect(false)}
        />
      )}
    </section>
  );
}
